import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reasons = [
  { icon: 'ri-shield-star-line', title: 'Resmi ManageEngine Partner', desc: "Türkiye'de yetkili partner statüsüyle lisanslı kurulum ve teknik destek.", color: '#0891B2' },
  { icon: 'ri-puzzle-line', title: 'Çok Markalı Entegrasyon', desc: 'ManageEngine, AutomMate ve Zoho ONE ekosisteminde tek çatı çözümü.', color: '#F97316' },
  { icon: 'ri-graduation-cap-line', title: 'Kurulum + Eğitim + Destek', desc: 'Projeden sonrasına kadar uçtan uca hizmet ve sürekli bakım garantisi.', color: '#0891B2' },
  { icon: 'ri-time-line', title: '12+ Yıl Deneyim', desc: 'Kurumsal IT projelerinde on yılı aşkın kanıtlanmış sektör tecrübesi.', color: '#F97316' },
  { icon: 'ri-rocket-line', title: '400+ Proje', desc: "Farklı sektörlerden 400'ü aşkın kurumsal IT projesi başarıyla tamamlandı.", color: '#0891B2' },
  { icon: 'ri-star-line', title: 'Güçlü Referanslar', desc: "Türkiye'nin önde gelen kurumlarından güçlü müşteri referansları.", color: '#F97316' },
  { icon: 'ri-24-hours-line', title: '7/24 Destek', desc: 'Kesintisiz teknik destek ve acil müdahale opsiyonuyla her an yanınızdayız.', color: '#0891B2' },
  { icon: 'ri-global-line', title: 'Sektör Bağımsız', desc: 'Finans, üretim, sağlık, kamu ve e-ticaret sektörlerinde kanıtlanmış deneyim.', color: '#F97316' },
];

const stats = [
  { value: '12+', label: 'Yıl Deneyim', icon: 'ri-time-line' },
  { value: '400+', label: 'Tamamlanan Proje', icon: 'ri-rocket-line' },
  { value: '3', label: 'Lider Teknoloji Platformu', icon: 'ri-apps-line' },
  { value: '7/24', label: 'Teknik Destek', icon: 'ri-headphone-line' },
];

const WhyWotech = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.ww-header', { scrollTrigger: { trigger: '.ww-header', start: 'top 85%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
      gsap.from('.ww-stat', { scrollTrigger: { trigger: '.ww-stats', start: 'top 80%' }, opacity: 0, y: 20, duration: 0.6, stagger: 0.1, ease: 'power3.out' });
      gsap.from('.ww-card', { scrollTrigger: { trigger: '.ww-grid', start: 'top 70%' }, opacity: 0, scale: 0.95, y: 20, duration: 0.6, stagger: 0.08, ease: 'back.out(1.2)' });
      gsap.from('.ww-cta', { scrollTrigger: { trigger: '.ww-cta', start: 'top 80%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="why" className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="ww-header text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-5 font-body">
            <i className="ri-medal-line"></i>
            Neden Biz?
          </div>
          <h2 className="text-4xl font-extrabold text-[#0F172A] mb-4 font-heading leading-tight">
            Neden <span className="text-[#0891B2]">WOTECH</span>?
          </h2>
          <p className="text-base text-[#475569] max-w-xl mx-auto font-body">
            Güvenilir, deneyimli ve çözüm odaklı IT partneriniz.
          </p>
        </div>

        {/* Stats bar */}
        <div className="ww-stats grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="ww-stat text-center p-6 rounded-2xl bg-white border border-[#E2E8F0]">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ECFEFF] mx-auto mb-3">
                <i className={`${s.icon} text-xl text-[#0891B2]`}></i>
              </div>
              <div className="text-3xl font-extrabold text-[#0F172A] font-heading">{s.value}</div>
              <div className="text-sm text-[#64748B] mt-1 font-body">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Feature cards */}
        <div className="ww-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {reasons.map((r, i) => (
            <article key={i} className="ww-card group p-5 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#0891B2]/30 transition-all duration-200 cursor-default">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4 transition-transform duration-200 group-hover:scale-110" style={{ background: `${r.color}12` }}>
                <i className={`${r.icon} text-lg`} style={{ color: r.color }}></i>
              </div>
              <h3 className="text-sm font-bold text-[#0F172A] mb-1.5 font-heading leading-snug">{r.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed font-body">{r.desc}</p>
            </article>
          ))}
        </div>

        {/* CTA block */}
        <div className="ww-cta relative rounded-2xl overflow-hidden bg-[#0891B2] p-10 md:p-14 text-center">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)' }} />
          <h3 className="relative text-3xl font-extrabold text-white mb-3 font-heading">IT Dönüşümünüze Bugün Başlayın</h3>
          <p className="relative text-base text-white/80 mb-8 max-w-lg mx-auto font-body">
            Uzman ekibimizle ücretsiz danışmanlık görüşmesi ayarlayın, ihtiyaçlarınızı birlikte değerlendirelim.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#0891B2] font-bold rounded-lg hover:bg-[#F8FAFC] transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer font-body">
            <i className="ri-phone-line text-lg"></i>
            Ücretsiz Danışmanlık Al
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyWotech;
